import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Grid, 
  TextField, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  LinearProgress,
  FormHelperText
} from '@mui/material';
import { 
  AutoAwesome as GenerateIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PersonOutlineOutlined as PersonIcon,
  WorkOutlineOutlined as ProjectIcon,
  DescriptionOutlined as NotesIcon,
  CheckCircleOutline as StatusIcon,
  StraightenOutlined as LengthIcon,
  BarChartOutlined as DetailIcon,
  StarOutlineOutlined as QualityIcon,
  AutoAwesomeOutlined as SparkleIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

import ProjectTypeTooltip from './ProjectTypeTooltip';
import AsyncJobProgress from './AsyncJobProgress';
import FileUploadSection from './FileUploadSection';
import ArchitectureDiagramUpload from './ArchitectureDiagramUpload';
import useAsyncJob from '../hooks/useAsyncJob';
import { sowService } from '../services/sowService';
import { 
  getFieldValidationStatus, 
  getCharacterCountColor, 
  validateScopingNotes,
  getContentQualityLevel,
  validationRules 
} from '../utils/formValidation';

const SOWGenerator = () => {
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);   // { file, s3Key }
  const [uploadError, setUploadError] = useState(null);
  const [uploadedDiagram, setUploadedDiagram] = useState(null);  // { file, s3Key }
  const [diagramUploadError, setDiagramUploadError] = useState(null);
  
  // Use the async job hook for managing SOW generation
  const {
    jobState,
    isSubmitting,
    isPolling,
    isCompleted,
    isFailed,
    isActive,
    progressPercentage,
    formattedElapsedTime,
    startJob,
    resetJob,
    downloadDocument,
    stopPolling
  } = useAsyncJob();

  const { control, handleSubmit, formState: { errors, isValid }, reset, watch } = useForm({
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      customerName: '',
      projectType: '',
      scopingNotes: '',
      projectTitle: '',
      estimatedDuration: '',
      budgetRange: ''
    }
  });

  // Watch form values
  const watchedValues = watch();
  const scopingNotesValue = watch('scopingNotes') || '';
  const customerNameValue = watch('customerName') || '';

  // Project types with detailed descriptions
  const projectTypes = [
    { 
      value: 'POC', 
      label: 'Proof of Concept (POC)',
      description: 'Quick validation of technical feasibility. Best for exploring new technologies or approaches before full commitment.',
      characteristics: ['2-4 weeks', '3 phases only', 'Minimal scope', 'Fail-fast approach']
    },
    { 
      value: 'T&M', 
      label: 'Time & Materials',
      description: 'Flexible scope with hourly billing. Best for exploratory projects where requirements may evolve.',
      characteristics: ['Flexible scope', 'Hourly billing', 'Iterative approach']
    },
    { 
      value: 'Agile', 
      label: 'Agile Development',
      description: 'Sprint-based delivery with iterative development. Ideal for software development projects.',
      characteristics: ['Sprint-based', 'Iterative delivery', 'Regular feedback cycles']
    },
    { 
      value: 'Fixed Price', 
      label: 'Fixed Price',
      description: 'Scope-bound deliverables with fixed cost. Best for well-defined projects with clear requirements.',
      characteristics: ['Fixed scope', 'Predetermined cost', 'Clear deliverables']
    }
  ];

  // Validation helpers
  const getValidationIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckIcon color="success" fontSize="small" />;
      case 'error': return <ErrorIcon color="error" fontSize="small" />;
      case 'empty': return <WarningIcon color="warning" fontSize="small" />;
      default: return null;
    }
  };

  const handleFileSelect = (file, error) => {
    if (error) { setUploadError(error); setUploadedFile(null); return; }
    setUploadedFile({ file, s3Key: null });
    setUploadError(null);
  };

  const handleFileClear = () => {
    setUploadedFile(null);
    setUploadError(null);
  };

  const handleDiagramSelect = (file, error) => {
    if (error) { setDiagramUploadError(error); setUploadedDiagram(null); return; }
    setUploadedDiagram({ file, s3Key: null });
    setDiagramUploadError(null);
  };

  const handleDiagramClear = () => {
    setUploadedDiagram(null);
    setDiagramUploadError(null);
  };

  const onSubmit = async (data) => {
    console.group('🎯 SOW FORM SUBMISSION');
    console.log('🖱️  SOWGenerator.js: Form submitted by user');
    console.log('📝 Form Data:', {
      customerName: data.customerName,
      projectType: data.projectType,
      scopingNotesLength: data.scopingNotes?.length || 0,
      projectTitle: data.projectTitle,
      estimatedDuration: data.estimatedDuration,
      budgetRange: data.budgetRange
    });
    
    try {
      console.log('📞 SOWGenerator.js: Starting async SOW generation job');
      
      // If a file was selected, upload it to S3 first
      let scopingNotesS3Key = null;
      
      // Validate: must have either text notes or an uploaded file
      if (!data.scopingNotes && !uploadedFile?.file) {
        setNotification({
          open: true,
          message: 'Please enter scoping notes or upload a file.',
          severity: 'warning'
        });
        return;
      }

      if (uploadedFile?.file) {
        console.log('📎 Uploading scoping notes file to S3...');
        setNotification({ open: true, message: 'Uploading file...', severity: 'info' });
        const { upload_url, s3_key } = await sowService.getUploadUrl(
          uploadedFile.file.name,
          uploadedFile.file.type || 'application/octet-stream'
        );
        await sowService.uploadFileToS3(upload_url, uploadedFile.file);
        scopingNotesS3Key = s3_key;
        console.log('✅ File uploaded, s3_key:', s3_key);
      }

      // Upload architecture diagram if provided
      let architectureDiagramS3Key = null;
      if (uploadedDiagram?.file) {
        console.log('🏗️ Uploading architecture diagram to S3...');
        setNotification({ open: true, message: 'Uploading architecture diagram...', severity: 'info' });
        const { upload_url, s3_key } = await sowService.getUploadUrl(
          uploadedDiagram.file.name,
          uploadedDiagram.file.type || 'image/png'
        );
        await sowService.uploadFileToS3(upload_url, uploadedDiagram.file);
        architectureDiagramS3Key = s3_key;
        setNotification({ open: true, message: '✅ Architecture diagram uploaded — will be embedded in Section 4.', severity: 'success' });
        console.log('✅ Architecture diagram uploaded, s3_key:', s3_key);
      }

      const result = await startJob({
        customer_name: data.customerName,
        project_type: data.projectType,
        scoping_notes: data.scopingNotes || undefined,
        scoping_notes_s3_key: scopingNotesS3Key || undefined,
        architecture_diagram_s3_key: architectureDiagramS3Key || undefined,
        project_title: data.projectTitle || null,
        estimated_duration: data.estimatedDuration || null,
        budget_range: data.budgetRange || null
      });
      if (result.success) {
        if (result.async) {
          console.log('🎉 SOWGenerator.js: Async SOW generation started!');
          console.log('📄 Job ID:', result.jobId);
          setNotification({
            open: true,
            message: 'SOW generation started! You can track progress below.',
            severity: 'info'
          });
        } else {
          console.log('🎉 SOWGenerator.js: Sync SOW generation completed!');
          setNotification({
            open: true,
            message: `SOW generated successfully in ${result.result?.generation_time?.toFixed(2)}s!`,
            severity: 'success'
          });
        }
      }
    } catch (error) {
      console.error('💥 SOWGenerator.js: Error in form submission:', error);
      
      // Enhanced error handling with specific error types
      let errorMessage = 'An unexpected error occurred';
      let errorSeverity = 'error';
      
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again with shorter scoping notes or check your connection.';
      } else if (error.message.includes('validation')) {
        errorMessage = 'Please check your form inputs and try again.';
        errorSeverity = 'warning';
      } else if (error.message.includes('network') || error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = `Error generating SOW: ${error.message}`;
      }
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: errorSeverity
      });
    } finally {
      console.groupEnd();
    }
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      const result = await downloadDocument();
      
      setNotification({
        open: true,
        message: `Document "${result.filename}" downloaded successfully!`,
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: `Download failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleReset = () => {
    resetJob();
    const defaultValues = {
      customerName: '',
      projectType: '',
      scopingNotes: '',
      projectTitle: '',
      estimatedDuration: '',
      budgetRange: ''
    };
    reset(defaultValues);
    setNotification({
      open: true,
      message: 'Form cleared successfully',
      severity: 'info'
    });
  };

  const handleCancel = () => {
    stopPolling();
    resetJob();
    setNotification({
      open: true,
      message: 'SOW generation cancelled. The background job may still complete — you can check Documents later.',
      severity: 'warning'
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, position: 'relative' }} className="fade-in">
      {/* BCE Background Blobs */}
      <div className="eclipse" style={{ width: '556px', height: '556px', top: '73px', left: '40px' }} />
      <div className="planet" style={{ width: '524px', height: '524px', top: '89px', left: 'calc(50% - 261px)' }} />
      <div className="planet1" style={{ width: '592px', height: '592px', top: '400px', right: '40px' }} />
      
      {/* BCE Hero Section with Glass UI */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          mb: 8,
          py: { xs: 6, md: 10 },
          px: 3,
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px 0 rgba(89, 92, 234, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(135deg, rgba(89, 92, 234, 0.1) 0%, rgba(103, 138, 231, 0.05) 50%, rgba(79, 193, 246, 0.1) 100%)
            `,
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: `
              radial-gradient(circle at 30% 50%, rgba(89, 92, 234, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 70% 50%, rgba(79, 193, 246, 0.08) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Chip
          icon={<SparkleIcon sx={{ fontSize: 16, color: '#ffffff !important' }} />}
          label="AI-Powered by BizCloud Experts"
          size="small"
          sx={{
            mb: 3,
            background: '#595bea80',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: '#ffffffff',
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 600,
            fontSize: '0.9rem',
            border: '1px solid rgba(89, 92, 234, 0.2)',
            px: 1.5, 
            py: 2.5,
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 4px 12px rgba(89, 92, 234, 0.1)',
            '& .MuiChip-icon': {
              color: '#ffffff !important',
            }
          }}
        />
        
        <Typography 
          variant="h1" 
          component="h1" 
          sx={{ 
            fontFamily: 'Poppins, sans-serif',
            fontSize: { xs: '2.25rem', md: '3.5rem' },
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: '#ffffffff',
            mb: 3,
            maxWidth: '900px',
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Create Professional SOWs in{' '}
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(135deg, #4e3bcbff 0%, #595cea 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Minutes, Not Hours
          </Box>
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            fontFamily: 'DM Sans, sans-serif',
            fontSize: { xs: '1.0625rem', md: '1.1875rem' },
            color: '#1e1e1e',
            lineHeight: 1.7,
            maxWidth: '700px',
            mx: 'auto',
            mb: 5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Transform your project scoping notes into comprehensive, professional
          Statements of Work using advanced AI technology.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Chip 
            icon={<CheckIcon sx={{ fontSize: 16, color: '#595cea' }} />}
            label="AI-Powered Analysis" 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#1e1e1e',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              px: 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
              },
            }} 
          />
          <Chip 
            icon={<CheckIcon sx={{ fontSize: 16, color: '#595cea' }} />}
            label="Fast Generation" 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#1e1e1e',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              px: 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
                background: 'rgba(255, 255, 255, 0.7)',
              },
            }} 
          />
          <Chip 
            icon={<CheckIcon sx={{ fontSize: 16, color: '#595cea' }} />}
            label="Professional Output" 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#1e1e1e',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              px: 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
                background: 'rgba(255, 255, 255, 0.7)',
              },
            }} 
          />
        </Box>
      </Box>

      <Paper 
        elevation={0} 
        className="glass-card slide-up"
        sx={{ 
          p: { xs: 4, md: 6 }, 
          mb: 4,
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 8px 32px 0 rgba(89, 92, 234, 0.08)',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 5 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2rem' },
                color: '#1e1e1e',
                mb: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              Project Details
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: 'DM Sans, sans-serif',
                color: 'rgba(30, 30, 30, 0.7)',
                fontSize: '1.0625rem',
                lineHeight: 1.6,
              }}
            >
              Provide your project information and let AI generate a comprehensive SOW
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Customer Name */}
            <Grid item xs={12} md={6}>
              <Controller
                name="customerName"
                control={control}
                rules={validationRules.customerName}
                render={({ field }) => (
                  <Box>
                    <TextField
                      {...field}
                      label="Customer Name"
                      fullWidth
                      required
                      error={!!errors.customerName}
                      helperText={errors.customerName?.message || 'Enter the full customer/company name'}
                      InputProps={{
                        endAdornment: getValidationIcon(getFieldValidationStatus(errors, 'customerName', customerNameValue))
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#f9fafb',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: '#ffffff',
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#ffffff',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          color: '#374151',
                        },
                      }}
                    />
                  </Box>
                )}
              />
            </Grid>

            {/* Project Type */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                required 
                error={!!errors.projectType}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#f9fafb',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                    color: '#374151',
                  },
                }}
              >
                <InputLabel id="project-type-label">Project Type</InputLabel>
                <Controller
                  name="projectType"
                  control={control}
                  rules={validationRules.projectType}
                  render={({ field }) => (
                    <Select 
                      {...field} 
                      label="Project Type"
                      labelId="project-type-label"
                      aria-label="Project Type"
                    >
                      {projectTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{type.label}</span>
                            <ProjectTypeTooltip projectType={type} />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.projectType && (
                  <FormHelperText>{errors.projectType.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Project Title */}
            <Grid item xs={12} md={6}>
              <Controller
                name="projectTitle"
                control={control}
                rules={validationRules.projectTitle}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Project Title (Optional)"
                    fullWidth
                    error={!!errors.projectTitle}
                    helperText={errors.projectTitle?.message || 'Descriptive title for the project'}
                    placeholder="e.g., Cloud Migration & Modernization"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#f9fafb',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: '#ffffff',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#374151',
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Estimated Duration */}
            <Grid item xs={12} md={6}>
              <Controller
                name="estimatedDuration"
                control={control}
                rules={validationRules.estimatedDuration}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Estimated Duration (Optional)"
                    fullWidth
                    error={!!errors.estimatedDuration}
                    helperText={errors.estimatedDuration?.message || 'Expected project timeline'}
                    placeholder="e.g., 3 months, 12 weeks, 6-8 weeks"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.12)',
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Budget Range */}
            <Grid item xs={12} md={6}>
              <Controller
                name="budgetRange"
                control={control}
                rules={validationRules.budgetRange}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Budget Range (Optional)"
                    fullWidth
                    error={!!errors.budgetRange}
                    helperText={errors.budgetRange?.message || 'Estimated budget or range'}
                    placeholder="e.g., $50k - $100k, $75,000"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(37, 99, 235, 0.12)',
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Scoping Notes */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 1,
                  }}
                >
                  Scoping Notes
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  You can <strong>type your scoping notes</strong> in the text area below, <strong>upload a file</strong> (PDF, DOCX, or TXT) using the upload section, or do both — the file content will be combined with any typed notes.
                </Typography>
              </Box>

              <Controller
                name="scopingNotes"
                control={control}
                rules={validationRules.scopingNotes}
                render={({ field }) => {
                  const isFileUploaded = !!uploadedFile?.file;
                  const validation = validateScopingNotes(scopingNotesValue, 50, 10000, isFileUploaded);
                  const charCount = scopingNotesValue.length;
                  const charCountColor = getCharacterCountColor(charCount, 50, 10000);
                  const qualityLevel = getContentQualityLevel(charCount);
                  
                  return (
                    <Box>
                      <TextField
                        {...field}
                        label="Detailed Scoping Notes"
                        multiline
                        rows={10}
                        fullWidth
                        error={!!errors.scopingNotes || validation.status === 'error'}
                        helperText={
                          errors.scopingNotes?.message || 
                          validation.message || 
                          'Detailed notes from customer scoping call including requirements, current state, challenges, and desired outcomes'
                        }
                        placeholder="Enter comprehensive scoping notes from customer meetings, including:

🎯 Business Objectives & Goals
• Current business challenges and pain points
• Desired outcomes and success criteria
• Strategic objectives and KPIs

🔧 Technical Requirements
• Technical requirements and constraints  
• Integration requirements with existing systems
• Performance and scalability needs

📋 Project Details
• Timeline and budget considerations
• Stakeholder information and decision makers
• Compliance and security requirements
• Risk factors and mitigation strategies

💡 Additional Context
• Current state assessment
• Future state vision
• Dependencies and assumptions"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.12)',
                            },
                            '& textarea': {
                              fontSize: '0.95rem',
                              lineHeight: 1.6,
                            },
                          },
                        }}
                      />
                      
                      {/* Enhanced character count and progress indicator */}
                      <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        background: 'rgba(89, 92, 234, 0.03)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        border: '1px solid rgba(89, 92, 234, 0.1)',
                        boxShadow: '0 4px 12px rgba(89, 92, 234, 0.05)',
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body2" 
                              fontWeight={500}
                              color={charCountColor === 'error' ? 'error' : charCountColor === 'warning' ? 'warning.main' : 'success.main'}
                            >
                              {charCount.toLocaleString()} / 10,000 characters
                            </Typography>
                            {validation.status === 'valid' && <CheckIcon color="success" fontSize="small" />}
                            {validation.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Progress to minimum
                            </Typography>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min((charCount / 50) * 100, 100)}
                              sx={{ 
                                width: 80, 
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: charCount >= 50 ? 'success.main' : 'warning.main',
                                  borderRadius: 3,
                                }
                              }}
                            />
                          </Box>
                        </Box>
                        
                        {/* Enhanced quality indicators */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip 
                            icon={<LengthIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                            label={`Length: ${charCount >= 50 ? 'Sufficient' : 'Too Short'}`}
                            variant="outlined"
                            size="medium"
                            sx={{ 
                              fontFamily: 'DM Sans, sans-serif',
                              fontWeight: 500,
                              px: 1.5,
                              py: 2.5,
                              backgroundColor: charCount >= 50 ? 'rgba(180, 228, 206, 0.15)' : 'rgba(245, 221, 168, 0.15)',
                              borderColor: charCount >= 50 ? '#b4e4ce' : '#f5dda8',
                              color: '#1e1e1e',
                              '& .MuiChip-icon': {
                                color: '#1e1e1e',
                              }
                            }}
                          />
                          <Chip 
                            icon={<DetailIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                            label={`Detail Level: ${qualityLevel}`}
                            variant="outlined"
                            size="medium"
                            sx={{ 
                              fontFamily: 'DM Sans, sans-serif',
                              fontWeight: 500,
                              px: 1.5,
                              py: 2.5,
                              backgroundColor: qualityLevel === 'Comprehensive' ? 'rgba(180, 228, 206, 0.15)' : qualityLevel === 'Detailed' ? 'rgba(184, 217, 245, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                              borderColor: qualityLevel === 'Comprehensive' ? '#b4e4ce' : qualityLevel === 'Detailed' ? '#b8d9f5' : '#e5e5e5',
                              color: '#1e1e1e',
                              '& .MuiChip-icon': {
                                color: '#1e1e1e',
                              }
                            }}
                          />
                          <Chip 
                            icon={<QualityIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                            label={`Quality: ${charCount >= 200 ? 'High' : charCount >= 100 ? 'Medium' : 'Basic'}`}
                            variant="outlined"
                            size="medium"
                            sx={{ 
                              fontFamily: 'DM Sans, sans-serif',
                              fontWeight: 500,
                              px: 1.5,
                              py: 2.5,
                              backgroundColor: charCount >= 200 ? 'rgba(180, 228, 206, 0.15)' : charCount >= 100 ? 'rgba(184, 217, 245, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                              borderColor: charCount >= 200 ? '#b4e4ce' : charCount >= 100 ? '#b8d9f5' : '#e5e5e5',
                              color: '#1e1e1e',
                              '& .MuiChip-icon': {
                                color: '#1e1e1e',
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                }}
              />
            </Grid>

            {/* File Upload — Optional alternative/supplement to scoping notes */}
            <Grid item xs={12}>
              <FileUploadSection
                uploadedFile={uploadedFile}
                uploadError={uploadError}
                onFileSelect={handleFileSelect}
                onFileClear={handleFileClear}
              />
            </Grid>

            {/* Architecture Diagram Upload — Optional, embedded in Section 4 */}
            <Grid item xs={12}>
              <ArchitectureDiagramUpload
                uploadedDiagram={uploadedDiagram}
                uploadError={diagramUploadError}
                onFileSelect={handleDiagramSelect}
                onFileClear={handleDiagramClear}
              />
            </Grid>

            {/* Form Summary and Submit */}
            <Grid item xs={12}>
              <Box sx={{ 
                mt: 4, 
                mb: 4, 
                p: 3,
                background: 'rgba(249, 249, 249, 0.6)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(89, 92, 234, 0.1)',
                boxShadow: '0 4px 12px rgba(89, 92, 234, 0.05)',
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    color: '#1e1e1e',
                    mb: 2,
                  }}
                >
                  Form Summary
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                  <Chip 
                    icon={<PersonIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                    label={`Customer: ${customerNameValue || 'Not specified'}`}
                    variant="outlined"
                    size="medium"
                    sx={{ 
                      fontFamily: 'DM Sans, sans-serif', 
                      fontWeight: 500,
                      px: 1.5,
                      py: 2.5,
                      backgroundColor: customerNameValue ? 'rgba(180, 228, 206, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                      borderColor: customerNameValue ? '#b4e4ce' : '#e5e5e5',
                      color: '#1e1e1e',
                      '& .MuiChip-icon': {
                        color: '#1e1e1e',
                      }
                    }}
                  />
                  <Chip 
                    icon={<ProjectIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                    label={`Project Type: ${watchedValues.projectType || 'Not selected'}`}
                    variant="outlined"
                    size="medium"
                    sx={{ 
                      fontFamily: 'DM Sans, sans-serif', 
                      fontWeight: 500,
                      px: 1.5,
                      py: 2.5,
                      backgroundColor: watchedValues.projectType ? 'rgba(180, 228, 206, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                      borderColor: watchedValues.projectType ? '#b4e4ce' : '#e5e5e5',
                      color: '#1e1e1e',
                      '& .MuiChip-icon': {
                        color: '#1e1e1e',
                      }
                    }}
                  />
                  <Chip 
                    icon={<NotesIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                    label={`Scoping Notes: ${scopingNotesValue.length.toLocaleString()} chars`}
                    variant="outlined"
                    size="medium"
                    sx={{ 
                      fontFamily: 'DM Sans, sans-serif', 
                      fontWeight: 500,
                      px: 1.5,
                      py: 2.5,
                      backgroundColor: scopingNotesValue.length >= 50 ? 'rgba(180, 228, 206, 0.15)' : 'rgba(245, 221, 168, 0.15)',
                      borderColor: scopingNotesValue.length >= 50 ? '#b4e4ce' : '#f5dda8',
                      color: '#1e1e1e',
                      '& .MuiChip-icon': {
                        color: '#1e1e1e',
                      }
                    }}
                  />
                  <Chip 
                    icon={<StatusIcon sx={{ color: '#1e1e1e !important', fontSize: 16 }} />}
                    label={`Form Status: ${isValid ? 'Ready' : 'Incomplete'}`}
                    variant="outlined"
                    size="medium"
                    sx={{ 
                      fontFamily: 'DM Sans, sans-serif', 
                      fontWeight: 500,
                      px: 1.5,
                      py: 2.5,
                      backgroundColor: isValid ? 'rgba(180, 228, 206, 0.15)' : 'rgba(245, 184, 197, 0.15)',
                      borderColor: isValid ? '#b4e4ce' : '#f5b8c5',
                      color: '#1e1e1e',
                      '& .MuiChip-icon': {
                        color: '#1e1e1e',
                      }
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 5 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isActive || !isValid}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
                  sx={{ 
                    minWidth: 240,
                    py: 2,
                    px: 6,
                    fontSize: '1.0625rem',
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file))
                      ? 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)' 
                      : '#d1d5db',
                    color: (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file)) ? 'white' : '#6b7280',
                    boxShadow: (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file))
                      ? '0 8px 20px rgba(89, 92, 234, 0.35)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file)) ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                      transition: 'left 0.5s ease',
                    } : {},
                    '&:hover': {
                      background: (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file))
                        ? 'linear-gradient(135deg, #4a4dd6 0%, #5851e1 100%)' 
                        : '#d1d5db',
                      transform: (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file)) ? 'translateY(-2px)' : 'none',
                      boxShadow: (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file))
                        ? '0 12px 28px rgba(89, 92, 234, 0.45)' 
                        : 'none',
                    },
                    '&:hover::before': (isValid && !isActive && (scopingNotesValue.length >= 50 || !!uploadedFile?.file)) ? {
                      left: '100%',
                    } : {},
                    '&:disabled': {
                      background: '#d1d5db',
                      color: '#6b7280',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isSubmitting ? 'Starting Generation...' : 
                   isPolling ? 'Processing...' :
                   'Generate SOW Document'}
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleReset}
                  disabled={isActive}
                  sx={{
                    minWidth: 160,
                    py: 2,
                    px: 4,
                    fontSize: '1rem',
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: '#d1d5db',
                    color: '#1e1e1e',
                    borderWidth: '2px',
                    '&:hover': {
                      borderColor: '#595cea',
                      backgroundColor: 'rgba(89, 92, 234, 0.06)',
                      color: '#595cea',
                      borderWidth: '2px',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      borderColor: '#e5e5e5',
                      color: 'rgba(30, 30, 30, 0.5)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Clear Form
                </Button>
              </Box>
              
              {!isValid && !isActive && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Alert 
                    severity="info" 
                    icon={<InfoIcon />}
                    sx={{ 
                      display: 'inline-flex',
                      borderRadius: '12px',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #93c5fd',
                      color: '#1e40af',
                      '& .MuiAlert-icon': {
                        color: '#3b82f6',
                      },
                      fontWeight: 500,
                    }}
                  >
                    Please complete all required fields to generate your SOW document
                  </Alert>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Async Job Progress Display */}
      <AsyncJobProgress
        jobState={jobState}
        progressPercentage={progressPercentage}
        formattedElapsedTime={formattedElapsedTime}
        isActive={isActive}
        isCompleted={isCompleted}
        isFailed={isFailed}
        onDownload={handleDownload}
        onReset={handleReset}
        onCancel={handleCancel}
        downloadLoading={downloadLoading}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SOWGenerator;